import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { managementAPI } from '../../services/api';
import api from '../../services/api';
import '../../App.css';

function ManageUserPicks() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Selection
  const [users, setUsers] = useState([]);
  const [races, setRaces] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRaceId, setSelectedRaceId] = useState('');
  
  // Picks data
  const [picks, setPicks] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedPick, setSelectedPick] = useState(null);
  
  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editHorse1, setEditHorse1] = useState('');
  const [editHorse2, setEditHorse2] = useState('');
  const [editHorse3, setEditHorse3] = useState('');
  const [editComment, setEditComment] = useState('');
  const [raceHorses, setRaceHorses] = useState([]);

  // Create pick modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createHorse1, setCreateHorse1] = useState('');
  const [createHorse2, setCreateHorse2] = useState('');
  const [createHorse3, setCreateHorse3] = useState('');
  const [createComment, setCreateComment] = useState('');
  const [createRaceHorses, setCreateRaceHorses] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchRaces();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/management/users?status=approved');
      setUsers(response.data.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchRaces = async () => {
    try {
      const response = await api.get('/races');
      const racesData = response.data.data?.races || response.data.data || [];
      setRaces(Array.isArray(racesData) ? racesData : []);
    } catch (err) {
      console.error('Error fetching races:', err);
      setRaces([]);
    }
  };

  const handleLoadPicks = async () => {
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      setLoading(true);
      const params = selectedRaceId ? { raceId: selectedRaceId } : {};
      const response = await managementAPI.getUserPicks(selectedUserId, params);
      console.log('Picks response:', JSON.stringify(response.data.picks, null, 2));
      setPicks(response.data.picks || []);
      setUserInfo(response.data.userInfo);
      
      if (!response.data.picks || response.data.picks.length === 0) {
        setError('No picks found for this user/race combination');
      }
    } catch (err) {
      console.error('Error loading picks:', err);
      setError(err.response?.data?.error || 'Failed to load picks');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = async (pick) => {
    setSelectedPick(pick);
    const horseIds = pick.horseIds && Array.isArray(pick.horseIds) ? pick.horseIds : [];
    setEditHorse1(horseIds[0] || '');
    setEditHorse2(horseIds[1] || '');
    setEditHorse3(horseIds[2] || '');
    setEditComment(pick.comment || '');
    
    // Load race horses
    try {
      const response = await api.get(`/races/${pick.raceId}`);
      setRaceHorses(response.data.data.horses || []);
    } catch (err) {
      console.error('Error loading race horses:', err);
      setRaceHorses([]);
    }
    
    setShowEditModal(true);
  };

  const openCreateModal = async () => {
    if (!selectedUserId) {
      setError('Please select a user');
      return;
    }
    if (!selectedRaceId) {
      setError('Please select a race');
      return;
    }
    setCreateHorse1('');
    setCreateHorse2('');
    setCreateHorse3('');
    setCreateComment('');
    setCreateRaceHorses([]);
    setShowCreateModal(true);
    try {
      const response = await api.get(`/races/${selectedRaceId}`);
      setCreateRaceHorses(response.data.data.horses || []);
    } catch (err) {
      console.error('Error loading race horses for create:', err);
    }
  };

  const handleCreatePick = async () => {
    if (!createHorse1 || !createHorse2 || !createHorse3) {
      setError('Please select all 3 horses');
      return;
    }
    if (new Set([createHorse1, createHorse2, createHorse3]).size < 3) {
      setError('Please select 3 different horses');
      return;
    }
    try {
      setLoading(true);
      setError('');
      await managementAPI.createUserPick({
        userId: selectedUserId,
        raceId: selectedRaceId,
        horseIds: [createHorse1, createHorse2, createHorse3],
        comment: createComment,
      });
      setSuccess('Pick created successfully!');
      setShowCreateModal(false);
      await handleLoadPicks();
    } catch (err) {
      console.error('Error creating pick:', err);
      setError(err.response?.data?.error?.message || err.response?.data?.error || 'Failed to create pick');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePick = async () => {
    if (!editHorse1 || !editHorse2 || !editHorse3) {
      setError('Please select all 3 horses');
      return;
    }
    
    if (editHorse1 === editHorse2 || editHorse1 === editHorse3 || editHorse2 === editHorse3) {
      setError('Please select 3 different horses');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      await managementAPI.updateUserPick(selectedPick.id, {
        userId: selectedPick.userId,
        horseIds: [editHorse1, editHorse2, editHorse3],
        comment: editComment
      });
      
      setSuccess('Pick updated successfully!');
      setShowEditModal(false);
      
      // Reload picks
      await handleLoadPicks();
    } catch (err) {
      console.error('Error updating pick:', err);
      setError(err.response?.data?.error || 'Failed to update pick');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1>Manage User Picks</h1>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/admin')}
        >
          ← Back to Dashboard
        </button>
      </div>

      <p style={{ marginBottom: '2rem', color: '#666' }}>
        View and edit any user's picks, even after race completion.
      </p>

      {success && (
        <div style={{ 
          padding: '1rem', 
          marginBottom: '1rem', 
          backgroundColor: '#d4edda', 
          color: '#155724', 
          border: '1px solid #c3e6cb',
          borderRadius: '4px'
        }}>
          {success}
        </div>
      )}

      {error && (
        <div style={{ 
          padding: '1rem', 
          marginBottom: '1rem', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          border: '1px solid #f5c6cb',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}

      {/* Selection Form */}
      <div style={{ 
        background: 'white', 
        padding: '2rem', 
        borderRadius: '8px', 
        border: '2px solid #e0e0e0',
        marginBottom: '2rem'
      }}>
        <h3>Select User and Race</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              User *
            </label>
            <select 
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">-- Select User --</option>
              {Array.isArray(users) && users.map(user => (
                <option key={user.userId} value={user.userId}>
                  {user.displayName} ({user.email})
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Race (Optional - leave empty for all)
            </label>
            <select 
              value={selectedRaceId}
              onChange={(e) => setSelectedRaceId(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">-- All Races --</option>
              {Array.isArray(races) && races.map(race => (
                <option key={race.raceId} value={race.raceId}>
                  {race.raceName} - {new Date(race.raceDate).toLocaleDateString()}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
          <button
            className="btn btn-primary"
            onClick={handleLoadPicks}
            disabled={loading || !selectedUserId}
          >
            {loading ? 'Loading...' : 'Load Picks'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={openCreateModal}
            disabled={loading || !selectedUserId || !selectedRaceId}
            title={!selectedRaceId ? 'Select a race first' : ''}
          >
            + Create Pick
          </button>
        </div>
      </div>

      {/* Picks List */}
      {userInfo && (
        <div style={{ marginBottom: '1rem' }}>
          <h3>Picks for: {userInfo.name} ({userInfo.email})</h3>
        </div>
      )}

      {picks.length > 0 && (
        <div style={{ 
          background: 'white', 
          padding: '2rem', 
          borderRadius: '8px', 
          border: '2px solid #e0e0e0'
        }}>
          <h3>User Picks ({picks.length})</h3>
          
          <div style={{ marginTop: '1rem' }}>
            {Array.isArray(picks) && picks.map(pick => {
              const raceInfo = pick.raceInfo || {};
              console.log('Rendering pick:', pick.id, 'horseNames:', pick.horseNames, 'horseIds:', pick.horseIds);
              return (
                <div key={pick.id} style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div style={{ flex: 1 }}>
                      <h4>{raceInfo.name || pick.raceId}</h4>
                      <p style={{ color: '#666', fontSize: '0.9rem' }}>
                        {raceInfo.dateTime && new Date(raceInfo.dateTime).toLocaleString()}
                        {raceInfo.status && ` • Status: ${raceInfo.status}`}
                      </p>
                      
                      <div style={{ marginTop: '0.5rem' }}>
                        <strong>Horses:</strong> {
                          pick.horseNames && Array.isArray(pick.horseNames) 
                            ? pick.horseNames.join(', ') 
                            : (pick.horseIds && Array.isArray(pick.horseIds) ? pick.horseIds.join(', ') : 'N/A')
                        }
                      </div>
                      
                      {pick.comment && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong>Comment:</strong> {pick.comment}
                        </div>
                      )}
                      
                      {pick.updatedByAdmin && (
                        <div style={{ 
                          marginTop: '0.5rem', 
                          padding: '0.5rem', 
                          background: '#fff3cd', 
                          borderRadius: '4px',
                          fontSize: '0.9rem'
                        }}>
                          ⚠️ {pick.adminNote || 'Modified by admin'}
                        </div>
                      )}
                      
                      <p style={{ color: '#999', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                        Created: {pick.submittedAt ? new Date(pick.submittedAt).toLocaleString() : 'N/A'}
                        {pick.updatedAt && ` • Updated: ${new Date(pick.updatedAt).toLocaleString()}`}
                      </p>
                    </div>
                    
                    <button 
                      className="btn btn-primary"
                      onClick={() => openEditModal(pick)}
                      style={{ marginLeft: '1rem' }}
                    >
                      ✏️ Edit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Create Pick Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2>Create Pick for User</h2>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              User: {users.find(u => u.userId === selectedUserId)?.displayName || selectedUserId}<br />
              Race: {races.find(r => r.raceId === selectedRaceId)?.raceName || selectedRaceId}
            </p>

            {['Horse 1', 'Horse 2', 'Horse 3'].map((label, idx) => {
              const val = [createHorse1, createHorse2, createHorse3][idx];
              const setter = [setCreateHorse1, setCreateHorse2, setCreateHorse3][idx];
              return (
                <div key={label} style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                    {label} *
                  </label>
                  <select
                    value={val}
                    onChange={(e) => setter(e.target.value)}
                    style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="">-- Select Horse --</option>
                    {createRaceHorses.map(horse => (
                      <option
                        key={horse.horseId}
                        value={horse.horseId}
                        disabled={!!horse.scratched}
                      >
                        #{horse.postPosition} {horse.horseName}{horse.scratched ? ' (SCRATCHED)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })}

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Comment (Optional)
              </label>
              <textarea
                value={createComment}
                onChange={(e) => setCreateComment(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  minHeight: '80px'
                }}
                maxLength={500}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button
                className="btn btn-primary"
                onClick={handleCreatePick}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Submit Pick'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPick && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h2>Edit Pick</h2>
            <p style={{ color: '#666', marginBottom: '1rem' }}>
              Race: {selectedPick.raceInfo?.name || selectedPick.raceId}
            </p>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Horse 1 *
              </label>
              <select 
                value={editHorse1}
                onChange={(e) => setEditHorse1(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">-- Select Horse --</option>
                {Array.isArray(raceHorses) && raceHorses.map(horse => (
                  <option key={horse.horseId} value={horse.horseId}>
                    {horse.horseName} (#{horse.postPosition})
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Horse 2 *
              </label>
              <select 
                value={editHorse2}
                onChange={(e) => setEditHorse2(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">-- Select Horse --</option>
                {Array.isArray(raceHorses) && raceHorses.map(horse => (
                  <option key={horse.horseId} value={horse.horseId}>
                    {horse.horseName} (#{horse.postPosition})
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Horse 3 *
              </label>
              <select 
                value={editHorse3}
                onChange={(e) => setEditHorse3(e.target.value)}
                style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '4px' }}
              >
                <option value="">-- Select Horse --</option>
                {Array.isArray(raceHorses) && raceHorses.map(horse => (
                  <option key={horse.horseId} value={horse.horseId}>
                    {horse.horseName} (#{horse.postPosition})
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Comment (Optional)
              </label>
              <textarea
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
                style={{ 
                  width: '100%', 
                  padding: '0.75rem', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  minHeight: '80px'
                }}
                maxLength={500}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
              <button 
                className="btn btn-primary"
                onClick={handleUpdatePick}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageUserPicks;
